import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import GradedQuizSubmissionsTable from '@/tables/GradedQuizSubmissionsTable';
import PendingQuizSubmissionsTable from '@/tables/PendingQuizSubmissionsTable';
import { useQuizSubmissionsTabAccess } from '~/hooks/useStudentTabs';
import { GroupHeaderWrapper } from '~/styles/layoutElements';

/**
 * The group tab's two stacked submissions tables, in legacy's order and under
 * its headings. Both headings use the `students` help placement, which belongs
 * to the section, not the table.
 */
function QuizSubmissionsScreen() {
  const { t } = useTranslation();
  const { groupName } = Route.useParams();
  const { canView, isResolved } = useQuizSubmissionsTabAccess();
  const { data: groupData } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const groupId = groupData?.id ?? 0;

  // Painting the headings before the group type lands would flash them at a
  // viewer the next line refuses.
  if (!isResolved) return null;
  // Without this gate the endpoint's 403 would paint two empty tables, which
  // read as "no submissions" rather than a denial. The Students layout admits
  // the same group types today, but this mirrors a separate legacy declaration.
  if (!canView) return <NotFoundScreen />;

  return (
    <>
      <GroupHeaderWrapper>
        <TitleWithQuestion
          title={t('wbs.pending-submissions-2')}
          placement="students"
        />
      </GroupHeaderWrapper>
      <PendingQuizSubmissionsTable groupId={groupId} groupName={groupName} />
      <div style={{ height: 32 }} />
      <GroupHeaderWrapper>
        <TitleWithQuestion
          title={t('lessons.all-graded-failed-submissions')}
          placement="students"
        />
      </GroupHeaderWrapper>
      <GradedQuizSubmissionsTable groupId={groupId} groupName={groupName} />
    </>
  );
}

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/students/quiz-submissions/quizzes/',
)({
  component: QuizSubmissionsScreen,
});
